/**
 * AI Strategy Presentation - Main Application
 * Refactored for reusable, data-driven architecture
 */

import React, { useState } from 'react';
import { Analytics } from '@vercel/analytics/react';
import { TitleSlide } from './components/slides/TitleSlide';
import { ContentSlide } from './components/slides/ContentSlide';
import { ServiceLinesSlide } from './presentations/cookstreetIntro/customSlides/ServiceLinesSlide';
import { ThankYouSlide } from './presentations/cookstreetIntro/customSlides/ThankYouSlide';
import { slides } from './presentations/cookstreetIntro/slides';
import { SlideData } from './types/slideTypes';

// Custom slide component registry
const customSlideRegistry: Record<string, React.ComponentType<any>> = {
  ServiceLines: ServiceLinesSlide,
  ThankYou: ThankYouSlide,
};

const AIStrategyPresentation = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  // Navigation functions
  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const goToSlide = (index: number) => {
    if (index >= 0 && index < slides.length) {
      setCurrentSlide(index);
    }
  };

  // Navigation props passed to all slides
  const navigationProps = {
    currentSlide,
    totalSlides: slides.length,
    onPrevious: prevSlide,
    onNext: nextSlide,
    onGoToSlide: goToSlide,
  };

  // Get current slide data
  const currentSlideData: SlideData = slides[currentSlide];

  // Render appropriate slide component based on type
  const renderSlide = () => {
    switch (currentSlideData.type) {
      case 'title':
        return <TitleSlide data={currentSlideData} {...navigationProps} />;

      case 'content':
        return <ContentSlide data={currentSlideData} {...navigationProps} />;

      case 'custom':
        const CustomComponent = customSlideRegistry[currentSlideData.component];
        if (CustomComponent) {
          return <CustomComponent {...currentSlideData.props} {...navigationProps} />;
        }
        return (
          <div className="h-screen bg-white flex items-center justify-center">
            <p className="text-xl text-red-600">
              Error: Custom component "{currentSlideData.component}" not found
            </p>
          </div>
        );

      default:
        return (
          <div className="h-screen bg-white flex items-center justify-center">
            <p className="text-xl text-red-600">
              Error: Unknown slide type
            </p>
          </div>
        );
    }
  };

  return (
    <>
      <div
        className="min-h-screen bg-gray-900 flex items-center justify-center"
        style={{ padding: 'var(--slide-padding-y) 1rem' }}
      >
        <div
          className="w-full max-w-6xl relative bg-white shadow-2xl"
          style={{
            aspectRatio: '3/2',
            maxHeight: 'var(--slide-max-height)',
          }}
        >
          {renderSlide()}
        </div>
      </div>
      <Analytics />
    </>
  );
};

export default AIStrategyPresentation;
